import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { WriterVersionSummary } from '@/components/writer/WriterVersionScrubber';

/**
 * Loads the latest 20 writer_versions for a project and keeps the UI in sync
 * as `compile_status` transitions (pending → running → success/failed).
 *
 * Two mechanisms cooperate:
 *   1. Realtime — a `postgres_changes` subscription reloads on every change.
 *   2. Fallback polling — while any version is still `pending`/`running`, a
 *      short setInterval reloads the list. This makes the UI resilient if
 *      Realtime is ever unavailable (misconfigured publication, dropped
 *      socket, RLS mismatch). Costs nothing in the steady state: as soon as
 *      no version is in-flight, the interval clears itself.
 */
export function useWriterVersions(projectId: string | undefined) {
  const [versions, setVersions] = useState<WriterVersionSummary[]>([]);

  const load = useCallback(async () => {
    if (!projectId) return;
    const { data } = await supabase
      .from('writer_versions')
      .select('id, label, compile_status, created_at, artifact_source_key, marked, compiled_at')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })
      .limit(20);
    setVersions((data ?? []) as WriterVersionSummary[]);
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    void load();
    const channel = supabase
      .channel(`writer-versions:${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'writer_versions',
          filter: `project_id=eq.${projectId}`,
        },
        () => {
          void load();
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId, load]);

  // Fallback poller: only ticks while a compile is actually in flight.
  useEffect(() => {
    if (!projectId) return;
    const inFlight = versions.some(
      (v) => v.compile_status === 'pending' || v.compile_status === 'running',
    );
    if (!inFlight) return;
    const timer = setInterval(() => {
      void load();
    }, 4000);
    return () => clearInterval(timer);
  }, [projectId, versions, load]);

  return { versions, reload: load };
}
